// Market Data Service - Real-time data from Yahoo Finance and Investpy
import { yahooFinanceService, YahooFinanceQuote } from './yahooFinanceService';
import { investpyService, InvestingQuote } from './investpyService';
import { dataValidationService, ValidationReport } from './dataValidationService';

export interface FuturesData {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  sentiment: 'Bullish' | 'Bearish' | 'Neutral';
  volume?: number;
  lastUpdated: string;
}

export interface MarketSentimentData {
  symbol: string;
  name: string;
  sentiment: 'Bullish' | 'Bearish' | 'Neutral';
  confidence: number;
  price: number;
  change: number;
  changePercent: number;
  technicalIndicators: {
    rsi: number;
    macd: 'Bullish' | 'Bearish' | 'Neutral';
    movingAverage: 'Above' | 'Below';
  };
}

// Data source priority: Yahoo Finance (primary), Investpy (secondary), Mock (fallback)
type DataSource = 'yahoo' | 'investpy' | 'mock';

// Available symbols for market data
const AVAILABLE_SYMBOLS = [
  'DXY', 'EUR', 'GBP', 'CHF', 'AUD', 'NZD', 'CAD', 'JPY',
  'SP500', 'NASDAQ', 'DOW', 'GOLD', 'SILVER', 'COPPER', 'PLATINUM',
  'CRUDE', 'GASOLINE', 'NATGAS'
] as const;

type MarketSymbol = typeof AVAILABLE_SYMBOLS[number];

// Symbol display names
const SYMBOL_NAMES: Record<MarketSymbol, string> = {
  'DXY': 'DXY (USD)',
  'EUR': 'EUR Futures',
  'GBP': 'GBP Futures', 
  'CHF': 'CHF Futures',
  'AUD': 'AUD Futures',
  'NZD': 'NZD Futures',
  'CAD': 'CAD Futures',
  'JPY': 'JPY Futures',
  'SP500': 'S&P 500 Futures',
  'NASDAQ': 'NASDAQ Futures',
  'DOW': 'DOW JONES Futures',
  'GOLD': 'GOLD Futures',
  'SILVER': 'SILVER Futures',
  'COPPER': 'COPPER Futures',
  'PLATINUM': 'PLATINUM Futures',
  'CRUDE': 'CRUDE OIL Futures',
  'GASOLINE': 'GASOLINE Futures',
  'NATGAS': 'NATURAL GAS Futures'
};

class MarketDataService {
  private cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_DURATION = 30000; // 30 seconds cache for real-time updates
  private failureCount = new Map<DataSource, number>();
  private readonly MAX_FAILURES = 3;
  private circuitBreakerOpen = new Map<DataSource, boolean>();
  private lastFailureTime = new Map<DataSource, number>();
  private readonly CIRCUIT_BREAKER_TIMEOUT = 300000; // 5 minutes
  private currentDataSource: DataSource = 'yahoo';
  private lastValidationReport: ValidationReport | null = null;
  
  // Real-time data enabled - Yahoo Finance (primary) + Investpy (backup)
  private readonly REAL_DATA_ENABLED = true;
  private readonly DATA_QUALITY_CHECK_INTERVAL = 60000; // Check data quality every minute

  // Check if circuit breaker should be reset for a specific data source
  private checkCircuitBreaker(source: DataSource): void {
    const isOpen = this.circuitBreakerOpen.get(source) || false;
    const lastFailure = this.lastFailureTime.get(source) || 0;
    
    if (isOpen && Date.now() - lastFailure > this.CIRCUIT_BREAKER_TIMEOUT) {
      this.circuitBreakerOpen.set(source, false);
      this.failureCount.set(source, 0);
      console.log(`${source} circuit breaker reset - attempting API calls again`);
    }
  }

  // Record API failure and potentially open circuit breaker
  private recordFailure(source: DataSource): void {
    const currentFailures = this.failureCount.get(source) || 0;
    this.failureCount.set(source, currentFailures + 1);
    this.lastFailureTime.set(source, Date.now());
    
    if (currentFailures + 1 >= this.MAX_FAILURES) {
      this.circuitBreakerOpen.set(source, true);
      console.warn(`${source} circuit breaker opened after ${currentFailures + 1} failures`);
      
      // Switch to next available data source
      this.switchToNextDataSource();
    }
  }

  // Switch to the next available data source
  private switchToNextDataSource(): void {
    const sources: DataSource[] = ['yahoo', 'investpy', 'mock'];
    const currentIndex = sources.indexOf(this.currentDataSource);
    
    for (let i = 1; i < sources.length; i++) {
      const nextIndex = (currentIndex + i) % sources.length;
      const nextSource = sources[nextIndex];
      
      if (!this.circuitBreakerOpen.get(nextSource)) {
        this.currentDataSource = nextSource;
        console.log(`Switched to data source: ${nextSource}`);
        return;
      }
    }
    
    // If all sources are down, use mock
    this.currentDataSource = 'mock';
    console.log('All data sources failed, using mock data');
  }

  // Fetch data from Yahoo Finance
  private async fetchFromYahooFinance(symbol: MarketSymbol): Promise<FuturesData | null> {
    try {
      const quote = await yahooFinanceService.fetchQuote(symbol);
      if (!quote) return null;
      
      return {
        symbol,
        name: SYMBOL_NAMES[symbol],
        price: quote.regularMarketPrice,
        change: quote.regularMarketChange,
        changePercent: quote.regularMarketChangePercent,
        sentiment: this.calculateSentiment(quote.regularMarketPrice, quote.regularMarketChange, quote.regularMarketVolume),
        volume: quote.regularMarketVolume,
        lastUpdated: new Date(quote.regularMarketTime * 1000).toISOString()
      };
    } catch (error) {
      console.warn(`Yahoo Finance error for ${symbol}:`, error);
      throw error;
    }
  }

  // Fetch data from Investpy
  private async fetchFromInvestpy(symbol: MarketSymbol): Promise<FuturesData | null> {
    try {
      const quote = await investpyService.fetchQuote(symbol);
      if (!quote) return null;
      
      return {
        symbol,
        name: SYMBOL_NAMES[symbol],
        price: quote.price,
        change: quote.change,
        changePercent: quote.changePercent,
        sentiment: this.calculateSentiment(quote.price, quote.change, quote.volume),
        volume: quote.volume,
        lastUpdated: new Date(quote.timestamp).toISOString()
      };
    } catch (error) {
      console.warn(`Investpy error for ${symbol}:`, error);
      throw error;
    }
  }

  // Fetch data from the current data source
  private async fetchFromCurrentSource(symbol: MarketSymbol): Promise<FuturesData | null> {
    switch (this.currentDataSource) {
      case 'yahoo':
        return await this.fetchFromYahooFinance(symbol);
      case 'investpy':
        return await this.fetchFromInvestpy(symbol);
      case 'mock':
        return this.getMockDataForSymbol(symbol);
      default:
        throw new Error(`Unknown data source: ${this.currentDataSource}`);
    }
  }

  // Fallback to mock data if APIs fail - Updated with current real market prices
  getMockFuturesData(): FuturesData[] {
    return [
      { symbol: 'DXY', name: 'DXY (USD)', price: 106.45, change: 0.23, changePercent: 0.22, sentiment: 'Bullish', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'EUR', name: 'EUR Futures', price: 1.0542, change: -0.0012, changePercent: -0.11, sentiment: 'Bearish', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'GBP', name: 'GBP Futures', price: 1.2634, change: 0.0045, changePercent: 0.36, sentiment: 'Bullish', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'CHF', name: 'CHF Futures', price: 0.8945, change: -0.0012, changePercent: -0.13, sentiment: 'Neutral', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'AUD', name: 'AUD Futures', price: 0.6745, change: 0.0034, changePercent: 0.51, sentiment: 'Bullish', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'NZD', name: 'NZD Futures', price: 0.6234, change: -0.0018, changePercent: -0.29, sentiment: 'Bearish', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'CAD', name: 'CAD Futures', price: 0.7456, change: 0.0021, changePercent: 0.28, sentiment: 'Bullish', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'JPY', name: 'JPY Futures', price: 149.85, change: -0.45, changePercent: -0.30, sentiment: 'Bearish', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'SP500', name: 'S&P 500 Futures', price: 5845.20, change: 12.50, changePercent: 0.21, sentiment: 'Bullish', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'NASDAQ', name: 'NASDAQ Futures', price: 20567.80, change: -45.30, changePercent: -0.22, sentiment: 'Bearish', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'DOW', name: 'DOW JONES Futures', price: 37245.67, change: 156.23, changePercent: 0.42, sentiment: 'Bullish', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'GOLD', name: 'GOLD Futures', price: 2675.30, change: 15.80, changePercent: 0.59, sentiment: 'Bullish', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'SILVER', name: 'SILVER Futures', price: 31.25, change: 0.45, changePercent: 1.46, sentiment: 'Bullish', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'COPPER', name: 'COPPER Futures', price: 3.845, change: -0.025, changePercent: -0.65, sentiment: 'Bearish', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'PLATINUM', name: 'PLATINUM Futures', price: 945.50, change: 8.75, changePercent: 0.93, sentiment: 'Bullish', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'CRUDE', name: 'CRUDE OIL Futures', price: 68.45, change: -1.23, changePercent: -1.77, sentiment: 'Bearish', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'GASOLINE', name: 'GASOLINE Futures', price: 2.345, change: 0.045, changePercent: 1.96, sentiment: 'Bullish', volume: 0, lastUpdated: new Date().toISOString() },
      { symbol: 'NATGAS', name: 'NATURAL GAS Futures', price: 2.845, change: -0.125, changePercent: -4.21, sentiment: 'Bearish', volume: 0, lastUpdated: new Date().toISOString() }
    ];
  }

  // Get mock data for a single symbol
  private getMockDataForSymbol(symbol: MarketSymbol): FuturesData {
    const mockData = this.getMockFuturesData();
    const symbolData = mockData.find(data => data.symbol === symbol);
    
    if (!symbolData) {
      // Generate default mock data if symbol not found
      return {
        symbol,
        name: SYMBOL_NAMES[symbol],
        price: 100,
        change: 0,
        changePercent: 0,
        sentiment: 'Neutral',
        volume: 0,
        lastUpdated: new Date().toISOString()
      };
    }
    
    return symbolData;
  }

  // Calculate sentiment based on technical indicators
  calculateSentiment(price: number, change: number, volume?: number): 'Bullish' | 'Bearish' | 'Neutral' {
    const changePercent = (change / (price - change)) * 100;
    
    if (changePercent > 1) return 'Bullish';
    if (changePercent < -1) return 'Bearish';
    return 'Neutral';
  }

  // Get futures data with caching and multi-source fallback
  async getFuturesData(): Promise<FuturesData[]> {
    const cacheKey = 'futures_data';
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }

    // If real data is disabled, use mock data
    if (!this.REAL_DATA_ENABLED) {
      const mockData = this.getMockFuturesData();
      this.cache.set(cacheKey, { data: mockData, timestamp: Date.now() });
      console.log('Real data disabled: Using mock futures data');
      return mockData;
    }

    try {
      // Check if current source circuit breaker should be reset
      this.checkCircuitBreaker(this.currentDataSource);
      
      // If current source is down, try to switch
      if (this.circuitBreakerOpen.get(this.currentDataSource)) {
        this.switchToNextDataSource();
      }
      
      // Fetch data from current source for all symbols
      const symbols = AVAILABLE_SYMBOLS as readonly MarketSymbol[];
      const promises = symbols.map(async (symbol) => {
        try {
          const data = await this.fetchFromCurrentSource(symbol);
          return data;
        } catch (error) {
          console.warn(`Failed to fetch ${this.currentDataSource} data for ${symbol}:`, error);
          this.recordFailure(this.currentDataSource);
          return null;
        }
      });
      
      const results = await Promise.allSettled(promises);
      const validData = results
        .filter((result): result is PromiseFulfilledResult<FuturesData> => 
          result.status === 'fulfilled' && result.value !== null
        )
        .map(result => result.value);
      
      if (validData.length === 0) {
        throw new Error(`No valid data received from ${this.currentDataSource}`);
      }
      
      // Reset failure count on successful request
      this.failureCount.set(this.currentDataSource, 0);
      console.log(`${this.currentDataSource} data fetch successful: ${validData.length} symbols`);
      
      this.cache.set(cacheKey, { data: validData, timestamp: Date.now() });
      return validData;
      
    } catch (error) {
      console.warn(`Failed to fetch real futures data from ${this.currentDataSource}, trying fallback:`, error);
      this.recordFailure(this.currentDataSource);
      
      // Try fallback sources
      if (this.currentDataSource !== 'mock') {
        this.switchToNextDataSource();
        return await this.getFuturesData(); // Recursive call with new source
      }
      
      // Final fallback to mock data
      const mockData = this.getMockFuturesData();
      this.cache.set(cacheKey, { data: mockData, timestamp: Date.now() });
      return mockData;
    }
  }

  // Get market sentiment data using current data source
  async getMarketSentiment(): Promise<MarketSentimentData[]> {
    const cacheKey = 'market_sentiment';
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }

    try {
      const futuresData = await this.getFuturesData();
      
      const sentimentData: MarketSentimentData[] = futuresData.map(futures => ({
        symbol: futures.symbol,
        name: futures.name,
        sentiment: futures.sentiment,
        confidence: Math.abs(futures.changePercent) > 1 ? 85 : Math.abs(futures.changePercent) > 0.5 ? 70 : 55,
        price: futures.price,
        change: futures.change,
        changePercent: futures.changePercent,
        technicalIndicators: {
          rsi: Math.min(Math.max(50 + (futures.changePercent * 2), 0), 100), // RSI between 0-100
          macd: futures.changePercent > 0 ? 'Bullish' : futures.changePercent < 0 ? 'Bearish' : 'Neutral',
          movingAverage: futures.changePercent > 0 ? 'Above' : 'Below'
        }
      }));
      
      this.cache.set(cacheKey, { data: sentimentData, timestamp: Date.now() });
      return sentimentData;
    } catch (error) {
      console.error(`Error fetching market sentiment from ${this.currentDataSource}:`, error);
      
      // Return mock sentiment data as fallback
      const mockSentiment: MarketSentimentData[] = [
        {
          symbol: 'SP500',
          name: 'S&P 500 Futures',
          sentiment: 'Bullish',
          confidence: 78,
          price: 5845.20,
          change: 12.50,
          changePercent: 0.21,
          technicalIndicators: {
            rsi: 65,
            macd: 'Bullish',
            movingAverage: 'Above'
          }
        },
        {
          symbol: 'GOLD',
          name: 'GOLD Futures',
          sentiment: 'Bullish',
          confidence: 82,
          price: 2675.30,
          change: 15.80,
          changePercent: 0.59,
          technicalIndicators: {
            rsi: 72,
            macd: 'Bullish',
            movingAverage: 'Above'
          }
        }
      ];
      
      this.cache.set(cacheKey, { data: mockSentiment, timestamp: Date.now() });
      return mockSentiment;
    }
  }

  // Start real-time updates using current data source (every 30 seconds)
  startRealTimeUpdates(callback: (data: FuturesData[]) => void): () => void {
    console.log(`Starting real-time updates with ${this.currentDataSource} (30-second intervals)`);
    
    const updateInterval = setInterval(async () => {
      try {
        const data = await this.getFuturesData();
        callback(data);
        console.log(`${this.currentDataSource} update completed: ${data.length} symbols updated`);
      } catch (error) {
        console.error(`Error in ${this.currentDataSource} real-time update:`, error);
      }
    }, 30000); // Update every 30 seconds

    // Return cleanup function
    return () => {
      clearInterval(updateInterval);
      console.log(`Real-time updates stopped (was using ${this.currentDataSource})`);
    };
  }

  // Stop real-time updates
  stopRealTimeUpdates(intervalId: NodeJS.Timeout) {
    clearInterval(intervalId);
  }

  // Get current data source
  getCurrentDataSource(): DataSource {
    return this.currentDataSource;
  }

  // Manually switch data source
  switchDataSource(source: DataSource): void {
    if (source !== this.currentDataSource) {
      this.currentDataSource = source;
      console.log(`Manually switched to data source: ${source}`);
    }
  }

  // Get data source status
  getDataSourceStatus(): Record<DataSource, { available: boolean; failures: number }> {
    return {
      yahoo: {
        available: !this.circuitBreakerOpen.get('yahoo'),
        failures: this.failureCount.get('yahoo') || 0
      },
      investpy: {
        available: !this.circuitBreakerOpen.get('investpy'),
        failures: this.failureCount.get('investpy') || 0
      },
      mock: {
        available: true,
        failures: 0
      }
    };
  }

  // Reset all circuit breakers
  resetAllCircuitBreakers(): void {
    this.circuitBreakerOpen.clear();
    this.failureCount.clear();
    this.lastFailureTime.clear();
    this.currentDataSource = 'yahoo';
    console.log('All circuit breakers reset, switched back to Yahoo Finance');
  }

  // Validate data quality and generate report
  async validateDataQuality(): Promise<ValidationReport> {
    try {
      const futuresData = await this.getFuturesData();
      
      // Prepare data for validation
      const symbolsData = futuresData.map(data => ({
        symbol: data.symbol,
        price: data.price,
        timestamp: new Date(data.lastUpdated).getTime()
      }));
      
      // Generate validation report
      const report = await dataValidationService.generateValidationReport(symbolsData);
      this.lastValidationReport = report;
      
      // Log critical issues
      if (report.overallHealth === 'CRITICAL' || report.overallHealth === 'WARNING') {
        console.warn(`🚨 DATA QUALITY ALERT: ${report.overallHealth}`);
        console.warn(`Real-time data: ${report.realTimeCount}/${report.totalSymbols}`);
        
        // Switch data source if quality is too poor
        if (report.overallHealth === 'CRITICAL' && this.currentDataSource !== 'mock') {
          console.warn('🔄 Switching data source due to poor data quality');
          this.switchToNextDataSource();
        }
      }
      
      return report;
    } catch (error) {
      console.error('❌ Error validating data quality:', error);
      throw error;
    }
  }

  // Get latest validation report
  getLatestValidationReport(): ValidationReport | null {
    return this.lastValidationReport;
  }

  // Check if current data is real-time
  isDataRealTime(): boolean {
    return dataValidationService.isDataQualityAcceptable();
  }

  // Get symbols with data issues
  getProblematicSymbols(): string[] {
    return dataValidationService.getProblematicSymbols();
  }

  // Start automatic data quality monitoring
  startDataQualityMonitoring(): () => void {
    console.log('🔍 Starting automatic data quality monitoring');
    
    const monitoringInterval = setInterval(async () => {
      try {
        await this.validateDataQuality();
      } catch (error) {
        console.error('Error in data quality monitoring:', error);
      }
    }, this.DATA_QUALITY_CHECK_INTERVAL);
    
    // Return cleanup function
    return () => {
      clearInterval(monitoringInterval);
      console.log('🔍 Data quality monitoring stopped');
    };
  }

  // Force data refresh and validation
  async forceDataRefresh(): Promise<{ data: FuturesData[], validation: ValidationReport }> {
    console.log('🔄 Forcing data refresh and validation...');
    
    // Clear cache to force fresh data
    this.cache.clear();
    
    // Fetch fresh data
    const data = await this.getFuturesData();
    
    // Validate data quality
    const validation = await this.validateDataQuality();
    
    console.log(`✅ Data refresh completed: ${data.length} symbols, quality: ${validation.overallHealth}`);
    
    return { data, validation };
  }

  // Get data source health summary
  getHealthSummary(): {
    currentSource: DataSource;
    dataQuality: string;
    realTimeSymbols: number;
    totalSymbols: number;
    lastValidation: string | null;
    problematicSymbols: string[];
  } {
    const report = this.getLatestValidationReport();
    
    return {
      currentSource: this.currentDataSource,
      dataQuality: report?.overallHealth || 'UNKNOWN',
      realTimeSymbols: report?.realTimeCount || 0,
      totalSymbols: report?.totalSymbols || 0,
      lastValidation: report?.timestamp || null,
      problematicSymbols: this.getProblematicSymbols()
    };
  }
}

export const marketDataService = new MarketDataService();
export default marketDataService;